import { createAsyncThunk, createSlice, type Action, type PayloadAction } from "@reduxjs/toolkit"

import {
  getDashboardCatalog,
  getDashboardWidgets,
  resolveDashboardWidget
} from "../api/services.ts"
import {
  instanceValueKey,
  resolvedValueKey
} from "../components/Portfolio/widgetsResponseAdapter.ts"
import { EMPTY_GLOBAL_FILTERS } from "./globalFilters.ts"
import type { GlobalFilters, PortfolioState, PortfolioWindow } from "./portfolio.types.ts"
import type { ResolvedWidget, WidgetCatalog, WidgetInstanceV4 } from "./widgets.types.ts"

const unwrap = <T>(data: unknown): T | undefined => {
  const enveloped = (data as { details?: { data?: T } } | undefined)?.details?.data
  return enveloped ?? (data as T | undefined)
}

export type WidgetsStatus = "idle" | "loading" | "available" | "error"

export interface WidgetsSliceState {
  status: WidgetsStatus
  fellBack: boolean
  widgets: ResolvedWidget[]
  resolvedByKey: Record<string, ResolvedWidget>
  pendingResolveIds: Record<string, string>
  contextKey: string
  generatedAt: string | null
  catalog: WidgetCatalog | null
  catalogStatus: WidgetsStatus
  currentRequestId?: string
}

export const initialWidgetsState: WidgetsSliceState = {
  status: "idle",
  fellBack: false,
  widgets: [],
  resolvedByKey: {},
  pendingResolveIds: {},
  contextKey: "",
  generatedAt: null,
  catalog: null,
  catalogStatus: "idle",
  currentRequestId: undefined,
}

export const WIDGET_SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1000
export const MAX_WIDGET_SNAPSHOT_BYTES = 256 * 1024

interface PersistedWidgetSnapshot {
  version: 1
  widgets: ResolvedWidget[]
  contextKey: string
  generatedAt: string
}

export const isWidgetSnapshotFresh = (
  generatedAt: string | null,
  now = Date.now()
): boolean => {
  if (!generatedAt) return false
  const generatedTime = new Date(generatedAt).getTime()
  return Number.isFinite(generatedTime) && now - generatedTime <= WIDGET_SNAPSHOT_MAX_AGE_MS
}

export const serializeWidgetsSnapshot = (
  state: WidgetsSliceState,
  now = Date.now()
): PersistedWidgetSnapshot | null => {
  if (!state.widgets.length || !isWidgetSnapshotFresh(state.generatedAt, now))
    return null

  const snapshot: PersistedWidgetSnapshot = {
    version: 1,
    widgets: state.widgets,
    contextKey: state.contextKey,
    generatedAt: state.generatedAt as string,
  }
  const size = new TextEncoder().encode(JSON.stringify(snapshot)).byteLength
  return size <= MAX_WIDGET_SNAPSHOT_BYTES ? snapshot : null
}

export const rehydrateWidgetsSnapshot = (
  snapshot: unknown,
  now = Date.now()
): WidgetsSliceState => {
  const value = snapshot as Partial<PersistedWidgetSnapshot> | null
  if (
    !value ||
    value.version !== 1 ||
    !Array.isArray(value.widgets) ||
    !value.widgets.length ||
    typeof value.contextKey !== "string" ||
    typeof value.generatedAt !== "string" ||
    !isWidgetSnapshotFresh(value.generatedAt, now)
  ) {
    return initialWidgetsState
  }

  const resolvedByKey = Object.fromEntries(
    value.widgets.map((widget) => [resolvedValueKey(widget), widget])
  )
  return {
    ...initialWidgetsState,
    status: "available",
    widgets: value.widgets,
    resolvedByKey,
    contextKey: value.contextKey,
    generatedAt: value.generatedAt,
  }
}

const contextKeyOf = (
  window: PortfolioWindow,
  filters: GlobalFilters,
  teamScope: string[] = [],
  configKey?: string
): string => JSON.stringify({ window, filters, teamScope, configKey })

const SESSION_REPLACEMENT_ACTIONS = new Set([
  "auth/logout/fulfilled",
  "auth/signIn/fulfilled",
  "auth/createAccount/fulfilled",
  "auth/confirmEmail/fulfilled",
  "auth/socialAuth/fulfilled",
])

type FetchWidgetsResult =
  | { fellBack: true }
  | {
      fellBack: false
      widgets: ResolvedWidget[]
      contextKey: string
      generatedAt: string | null
    }

export const fetchWidgets = createAsyncThunk<FetchWidgetsResult, PortfolioWindow>(
  "widgets/fetchWidgets",
  async (window, { getState, rejectWithValue }) => {
    const portfolio = (getState() as { portfolio?: PortfolioState }).portfolio
    const filters = portfolio?.globalFilters ?? EMPTY_GLOBAL_FILTERS
    const teamScope = portfolio?.teamScope ?? []
    const configKey = portfolio?.configKey
    const response = await getDashboardWidgets(window, filters, teamScope, configKey)

    if (response.status === 404) {
      return { fellBack: true }
    }
    if (!response.ok) {
      return rejectWithValue(response.data)
    }

    const data = unwrap<{ widgets?: ResolvedWidget[]; generated_at?: string }>(
      response.data
    )
    if (!data || !Array.isArray(data.widgets)) {
      return rejectWithValue({ detail: "malformed widgets response" })
    }
    return {
      fellBack: false,
      widgets: data.widgets,
      contextKey: contextKeyOf(window, filters, teamScope, configKey),
      generatedAt: data.generated_at ?? null,
    }
  }
)

export const fetchCatalog = createAsyncThunk<WidgetCatalog | null>(
  "widgets/fetchCatalog",
  async (_, { rejectWithValue }) => {
    const response = await getDashboardCatalog()
    if (!response.ok) {
      return rejectWithValue(response.data)
    }
    return unwrap<WidgetCatalog>(response.data) ?? null
  }
)

interface ResolveWidgetArg {
  instance: WidgetInstanceV4
  window: PortfolioWindow
}

type ResolveWidgetResult =
  | { applied: false }
  | { applied: true; resolved: ResolvedWidget; contextKey: string }

const isResolvedWidget = (value: unknown): value is ResolvedWidget =>
  value != null &&
  typeof (value as ResolvedWidget).type === "string" &&
  (value as ResolvedWidget).payload != null

// Ad-hoc single-widget resolve: give a just-added widget its real value without
// waiting for Save + a full refetch. Missing/older endpoints (404) and errors
// resolve to a no-op so the card falls back to the skeleton-until-save behaviour.
export const resolveWidget = createAsyncThunk<ResolveWidgetResult, ResolveWidgetArg>(
  "widgets/resolveWidget",
  async ({ instance, window }, { getState }) => {
    const portfolio = (getState() as { portfolio?: PortfolioState }).portfolio
    const filters = portfolio?.globalFilters ?? EMPTY_GLOBAL_FILTERS
    const teamScope = portfolio?.teamScope ?? []
    const configKey = portfolio?.configKey
    const response = await resolveDashboardWidget(instance, window, filters, teamScope)
    if (response.status === 404 || !response.ok) {
      return { applied: false }
    }
    const resolved = unwrap<ResolvedWidget>(response.data)
    if (!isResolvedWidget(resolved)) {
      return { applied: false }
    }
    return {
      applied: true,
      resolved,
      contextKey: contextKeyOf(window, filters, teamScope, configKey)
    }
  }
)

const widgetsSlice = createSlice({
  name: "widgets",
  initialState: initialWidgetsState,
  reducers: {
    resetWidgets() {
      return initialWidgetsState
    },
    dropResolvedWidget(state, action: PayloadAction<string>) {
      delete state.resolvedByKey[action.payload]
      delete state.pendingResolveIds[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWidgets.pending, (state, action) => {
        state.status = isWidgetSnapshotFresh(state.generatedAt) && state.widgets.length
          ? "available"
          : "loading"
        state.currentRequestId = action.meta?.requestId
      })
      .addCase(fetchWidgets.fulfilled, (state, action) => {
        if (action.meta?.requestId !== state.currentRequestId) return
        if (action.payload.fellBack) {
          state.fellBack = true
          state.status = "available"
          return
        }
        const { widgets, contextKey, generatedAt } = action.payload
        state.fellBack = false
        state.status = "available"
        state.widgets = widgets
        state.generatedAt = generatedAt
        if (state.contextKey !== contextKey) {
          state.resolvedByKey = {}
          state.pendingResolveIds = {}
          state.contextKey = contextKey
        }
        for (const widget of widgets) {
          state.resolvedByKey[resolvedValueKey(widget)] = widget
        }
      })
      .addCase(fetchWidgets.rejected, (state, action) => {
        if (action.meta?.requestId !== state.currentRequestId) return
        state.status = "error"
      })
      .addCase(fetchCatalog.pending, (state) => {
        state.catalogStatus = "loading"
      })
      .addCase(fetchCatalog.fulfilled, (state, action) => {
        state.catalog = action.payload
        state.catalogStatus = action.payload ? "available" : "error"
      })
      .addCase(fetchCatalog.rejected, (state) => {
        state.catalogStatus = "error"
      })
      .addCase(resolveWidget.pending, (state, action) => {
        const key = instanceValueKey(action.meta.arg.instance)
        state.pendingResolveIds[key] = action.meta.requestId
      })
      .addCase(resolveWidget.fulfilled, (state, action) => {
        const key = instanceValueKey(action.meta.arg.instance)
        // A drop (widget removed) or a superseding resolve clears/replaces the
        // pending id; either way this response is stale and must be discarded.
        if (state.pendingResolveIds[key] !== action.meta.requestId) return
        delete state.pendingResolveIds[key]
        if (!action.payload.applied) return
        if (state.contextKey && state.contextKey !== action.payload.contextKey) return
        state.resolvedByKey[key] = action.payload.resolved
      })
      .addCase(resolveWidget.rejected, (state, action) => {
        const key = instanceValueKey(action.meta.arg.instance)
        if (state.pendingResolveIds[key] === action.meta.requestId) {
          delete state.pendingResolveIds[key]
        }
      })
      .addMatcher(
        (action: Action) => SESSION_REPLACEMENT_ACTIONS.has(action.type),
        () => initialWidgetsState
      )
  },
})

export const { resetWidgets, dropResolvedWidget } = widgetsSlice.actions

export default widgetsSlice
